/**
 * The salon features the agency turns on/off from its salon page (agency › Salons › a salon › Features), and the
 * copy every surface shows for them. An off feature leaves the salon's web rail, the mobile tabs and every link
 * into it; nothing here is a security boundary (staff permissions and plan gates stay that).
 */

export const SALON_FEATURE_KEYS = ['CALENDAR', 'TURNS', 'MARKETING', 'REPORTS', 'SALES'] as const;
export type SalonFeatureKey = (typeof SALON_FEATURE_KEYS)[number];

/**
 * The features whose "off" hides them from the salon. Sales & checkout is a mode instead: off is the normal
 * "checks out on its own POS" state, so it never counts toward "N features off".
 */
export const HIDEABLE_SALON_FEATURE_KEYS = [
  'CALENDAR',
  'TURNS',
  'MARKETING',
  'REPORTS',
] as const satisfies readonly SalonFeatureKey[];
export type HideableSalonFeatureKey = (typeof HIDEABLE_SALON_FEATURE_KEYS)[number];

/**
 * The menus the agency hides from a salon (the "N features off" the agency sees). Calendar and Turns are not in
 * it: they are add-ons the salon chooses (the setup chip names them), so an off one is not something the agency
 * took away.
 */
export const AGENCY_HIDDEN_MENU_KEYS = [
  'MARKETING',
  'REPORTS',
] as const satisfies readonly HideableSalonFeatureKey[];
export type AgencyHiddenMenuKey = (typeof AGENCY_HIDDEN_MENU_KEYS)[number];

/**
 * Mirrors the Prisma `SalonModule` enum: the features stored in `Organization.disabledModules`. Turns lives in
 * `TurnTrackerConfig.isEnabled` and Sales in `pointOfSaleMode`, so neither is listed.
 */
export const STORED_SALON_MODULES = [
  'CALENDAR',
  'MARKETING',
  'REPORTS',
] as const satisfies readonly HideableSalonFeatureKey[];
export type StoredSalonModule = (typeof STORED_SALON_MODULES)[number];

/** Whether each feature is on for one salon (`resolveSalonModules`); the API's `modules` object. */
export interface SalonModuleStates {
  calendar: boolean;
  turns: boolean;
  marketing: boolean;
  reports: boolean;
  sales: boolean;
}

/** The `SalonModuleStates` field that holds each feature's on/off. */
export const SALON_FEATURE_MODULE_FIELD: { readonly [K in SalonFeatureKey]: Lowercase<K> } = {
  CALENDAR: 'calendar',
  TURNS: 'turns',
  MARKETING: 'marketing',
  REPORTS: 'reports',
  SALES: 'sales',
};

export interface SalonFeatureLabel {
  name: string;
  description: string;
}

/** Name + one-line description per feature (approved agency salon page copy). */
export const SALON_FEATURE_LABELS: Record<SalonFeatureKey, SalonFeatureLabel> = {
  CALENDAR: { name: 'Calendar', description: 'Appointments, online booking and reminders' },
  TURNS: { name: 'Turns', description: 'Walk-in turn board for technicians' },
  MARKETING: { name: 'Marketing', description: 'Bio Page, offers, campaigns and reviews' },
  REPORTS: { name: 'Reports', description: 'Sales, client and marketing results' },
  SALES: { name: 'Sales & checkout', description: "Check out in Hayven instead of the salon's own POS" },
};

/** Parts of the salon that can never be turned off. Labels only, in menu order. */
export const ALWAYS_ON_FEATURES = ['Messages', 'Clients', 'Services', 'Staff', 'Settings'] as const;
